import React, { useState } from 'react';
import { useStore } from '../../store/StoreContext';
import { Input, Select } from '../../components/UI/Input';
import SafeIcon from '../../common/SafeIcon';
import { generateId, formatDuration } from '../../utils/helpers';
import { PhaseInstanceEditor } from './PhaseInstanceEditor';

const COLORS = ['#6C63FF', '#F5A623', '#10B981', '#EF4444', '#3B82F6', '#EC4899'];

export const Step3Projects = ({ project, setProject, currentProjectWfs, setCurrentProjectWfs }) => {
  const { state } = useStore();
  const [editingPhaseIdx, setEditingPhaseIdx] = useState(null);

  const workflows = Object.values(state.workflowTemplates || {});
  const bus = Object.values(state.businessUnits || {});

  const addWorkflow = (template) => {
    const newWf = {
      ...JSON.parse(JSON.stringify(template)),
      templateId: template.id,
      id: generateId('wi'),
      tasks: (template.tasks || []).map(task => ({
        ...JSON.parse(JSON.stringify(task)),
        id: generateId('pti'),
        status: 'todo'
      }))
    };
    setCurrentProjectWfs([...currentProjectWfs, newWf]);
  };

  const updatePhase = (updatedPhase) => {
    const newWfs = [...currentProjectWfs];
    newWfs[editingPhaseIdx] = { ...newWfs[editingPhaseIdx], ...updatedPhase };
    setCurrentProjectWfs(newWfs);
    setEditingPhaseIdx(null);
  };

  return (
    <div className="h-full flex gap-6">
      <div className="w-1/3 bg-base-800 rounded-xl border border-base-700 p-6 flex flex-col shadow-xl">
        <h2 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
          <SafeIcon name="Book" className="text-primary" />
          Workflow Templates
        </h2>
        <div className="flex-1 overflow-auto space-y-3 pr-2 custom-scrollbar">
          {workflows.map(wf => (
            <div
              key={wf.id}
              onClick={() => addWorkflow(wf)}
              className="bg-base-900 p-4 rounded-xl border border-base-700 cursor-pointer hover:border-accent/50 transition-all group scale-100 hover:scale-[1.02] active:scale-95"
            >
              <div className="flex justify-between items-center mb-1 text-white">
                <span className="font-bold text-sm">{wf.name}</span>
                <SafeIcon name="Plus" className="w-4 h-4 text-accent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider">
                {wf.tasks?.length || 0} tasks • {formatDuration(wf.tasks?.reduce((s, t) => s + (t.durationMinutes || 0), 0))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 bg-base-800 rounded-xl border border-base-700 p-6 flex flex-col overflow-y-auto shadow-xl custom-scrollbar">
        <h2 className="text-lg font-semibold mb-6 text-white flex items-center gap-2">
          <SafeIcon name="Settings" className="text-accent" />
          Project Configuration
        </h2>

        <div className="space-y-6 mb-8">
          <div className="grid grid-cols-2 gap-6">
            <Input
              label="Project Name"
              value={project.name}
              onChange={e => setProject({ ...project, name: e.target.value })}
              placeholder="e.g., Q2 Infrastructure Overhaul"
            />
            <Select
              label="Business Unit"
              value={project.businessUnitId}
              onChange={e => setProject({ ...project, businessUnitId: e.target.value })}
              options={bus.map(bu => ({ label: bu.name, value: bu.id }))}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-400">Project Description</label>
            <textarea
              className="bg-base-800 border border-base-700 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder-gray-600 min-h-[80px] resize-none"
              value={project.description || ''}
              onChange={e => setProject({ ...project, description: e.target.value })}
              placeholder="Briefly describe the goals and scope of this project..."
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <Input
              label="Target Start Date"
              type="date"
              value={project.startDate}
              onChange={e => setProject({ ...project, startDate: e.target.value })}
            />
            <Input
              label="Hard Deadline Date"
              type="date"
              value={project.deadline || ''}
              onChange={e => setProject({ ...project, deadline: e.target.value })}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-400 mb-2 block">Brand Accent Color</label>
            <div className="flex gap-2.5">
              {COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setProject({ ...project, color: c })}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${project.color === c ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 bg-base-900/50 rounded-2xl border-2 border-dashed border-base-700 p-6">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-[0.2em] mb-4">Project Sequence (Timeline Phases)</h3>
          {currentProjectWfs.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500 opacity-40 py-12">
              <SafeIcon name="Layers" className="w-12 h-12 mb-4" />
              <p className="text-sm italic">Drag or click workflows from the library to build your project.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {currentProjectWfs.map((wf, idx) => {
                const totalMins = wf.tasks?.reduce((s, t) => s + (t.durationMinutes || 0), 0) || 0;
                return (
                  <div
                    key={wf.id}
                    className="bg-base-800 p-4 rounded-xl border-l-4 flex justify-between items-center group shadow-lg hover:bg-base-700/50 transition-colors"
                    style={{ borderLeftColor: project.color }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded bg-base-900 border border-base-700 flex items-center justify-center text-[10px] font-mono font-bold text-gray-500">
                        {idx + 1}
                      </div>
                      <div>
                        <div className="font-bold text-gray-100 text-sm">{wf.name}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5 font-mono">
                          {wf.tasks?.length || 0} Tasks • Effort: {formatDuration(totalMins)}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingPhaseIdx(idx)}
                        className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                        title="Edit Phase Tasks"
                      >
                        <SafeIcon name="Edit3" className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCurrentProjectWfs(currentProjectWfs.filter((_, i) => i !== idx))}
                        className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all"
                        title="Remove Phase"
                      >
                        <SafeIcon name="X" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <PhaseInstanceEditor
        isOpen={editingPhaseIdx !== null}
        onClose={() => setEditingPhaseIdx(null)}
        phase={editingPhaseIdx !== null ? currentProjectWfs[editingPhaseIdx] : null}
        onSave={updatePhase}
      />
    </div>
  );
};